import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckIcon, WarningIcon, CreditCardIcon, ShieldCheckIcon, ClockCountdownIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDT } from "@/lib/tz";

/**
 * Billing panel — mounted inside Settings → Billing tab.
 * Company Admin can subscribe, upgrade/downgrade, cancel, and resume.
 */
export default function BillingPanel() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(14);
  const [graceDays, setGraceDays] = useState(7);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get("/billing/plans"), api.get("/billing/subscription")]);
      setPlans(p.data.plans || []);
      setTrialDays(p.data.trial_days || 14);
      setGraceDays(p.data.grace_days || 7);
      setSub(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load billing info");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";
  const startCheckout = async (lookup_key) => {
    if (!isAdmin) { toast.error("Only admins can change the subscription"); return; }
    setBusyKey(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBusyKey("");
    }
  };

  const cancelSub = async () => {
    if (!isAdmin) return;
    if (!window.confirm(`Cancel your subscription at the end of the current billing period? You'll keep access until ${sub?.current_period_end ? fmtDT(sub.current_period_end) : "then"}.`)) return;
    setBusyKey("cancel");
    try {
      await api.post("/billing/cancel", { at_period_end: true });
      toast.success("Subscription will cancel at period end");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusyKey(""); }
  };

  const resumeSub = async () => {
    setBusyKey("resume");
    try {
      await api.post("/billing/resume", {});
      toast.success("Subscription resumed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resume failed");
    } finally { setBusyKey(""); }
  };

  if (loading) return <div className="text-sm text-slate-500 font-mono p-6">Loading billing…</div>;

  // If Super Admin has marked this company as Free, no billing is required
  if (sub?.billing_mode === "free") {
    return (
      <div className="space-y-4" data-testid="billing-panel">
        <div className="border-2 rounded-sm p-5 bg-emerald-50 border-emerald-400" data-testid="billing-status-free">
          <div className="flex items-start gap-3">
            <ShieldCheckIcon size={26} weight="fill" className="flex-shrink-0 mt-0.5 text-emerald-700" />
            <div className="flex-1">
              <div className="section-eyebrow text-emerald-800 mb-0.5">/// Complimentary account</div>
              <div className="font-display text-lg font-black tracking-tight text-emerald-950">🎁 Free — no billing required</div>
              <div className="text-xs mt-1 text-emerald-800">
                Your account has been marked as complimentary by the Rep Fleet team. All features are unlocked and you will never be charged. Contact support if you have any questions.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-panel">
      <StatusCard sub={sub} graceDays={graceDays} isAdmin={isAdmin} onCancel={cancelSub} onResume={resumeSub} busyKey={busyKey} />

      <div className="grid gap-4 md:grid-cols-3">
        {plans.map((p) => (
          <PlanCard
            key={p.lookup_key}
            plan={p}
            current={sub}
            trialDays={trialDays}
            isAdmin={isAdmin}
            onSubscribe={() => startCheckout(p.lookup_key)}
            busy={busyKey === p.lookup_key}
          />
        ))}
      </div>

      <div className="text-[11px] font-mono text-slate-500 max-w-3xl">
        Payments processed by Stripe. Test card <b>4242 4242 4242 4242</b> (any future expiry, any CVC).
        First payment happens after the {trialDays}-day free trial ends.
      </div>
    </div>
  );
}

function StatusCard({ sub, graceDays, isAdmin, onCancel, onResume, busyKey }) {
  const s = sub?.status || "none";
  const isActive = s === "active" || s === "trialing";
  const isPastDue = s === "past_due";
  const isCanceled = s === "canceled";
  const willCancel = sub?.cancel_at_period_end;

  let tone = "slate", label = "No subscription", icon = CreditCardIcon;
  if (s === "trialing") { tone = "blue"; label = `Free trial · ends ${sub.trial_end ? fmtDT(sub.trial_end) : ""}`; icon = ClockCountdownIcon; }
  else if (s === "active" && !willCancel) { tone = "emerald"; label = "Active"; icon = ShieldCheckIcon; }
  else if (s === "active" && willCancel) { tone = "amber"; label = `Ends on ${sub.current_period_end ? fmtDT(sub.current_period_end) : "period end"}`; icon = WarningIcon; }
  else if (isPastDue) { tone = "amber"; label = "Past due — please update payment method"; icon = WarningIcon; }
  else if (isCanceled) { tone = "red"; label = "Canceled — features are read-only"; icon = WarningIcon; }
  const I = icon;

  const toneCls = {
    slate: "bg-slate-50 border-slate-300 text-slate-700",
    blue: "bg-blue-50 border-blue-400 text-blue-900",
    emerald: "bg-emerald-50 border-emerald-400 text-emerald-900",
    amber: "bg-amber-50 border-amber-400 text-amber-900",
    red: "bg-red-50 border-red-400 text-red-900",
  }[tone];

  return (
    <div className={`border-2 rounded-sm p-4 ${toneCls}`} data-testid={`billing-status-${s}`}>
      <div className="flex items-start gap-3">
        <I size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="section-eyebrow text-slate-600 mb-0.5">/// Subscription</div>
          <div className="font-display text-lg font-black tracking-tight">
            {sub?.plan_name ? `${sub.plan_name} — ${label}` : label}
          </div>
          {isPastDue && sub?.grace_ends_at && (
            <div className="text-xs mt-1 font-mono">
              Access locks on <b>{fmtDT(sub.grace_ends_at)}</b> ({graceDays}-day grace period).
            </div>
          )}
          {isActive && sub?.current_period_end && !isPastDue && (
            <div className="text-xs mt-1 font-mono text-slate-600">
              {willCancel ? "Ends" : "Next renewal"}: {fmtDT(sub.current_period_end)}
            </div>
          )}
        </div>
        {isAdmin && isActive && !willCancel && (
          <Button variant="outline" size="sm" onClick={onCancel} disabled={busyKey === "cancel"} className="rounded-sm border-slate-300" data-testid="billing-cancel-btn">
            {busyKey === "cancel" ? "Cancelling…" : "Cancel subscription"}
          </Button>
        )}
        {isAdmin && isActive && willCancel && (
          <Button size="sm" onClick={onResume} disabled={busyKey === "resume"} className="rounded-sm bg-emerald-700 hover:bg-emerald-800" data-testid="billing-resume-btn">
            {busyKey === "resume" ? "Resuming…" : "Resume subscription"}
          </Button>
        )}
      </div>
    </div>
  );
}

function PlanCard({ plan, current, trialDays, isAdmin, onSubscribe, busy }) {
  const isCurrent = current?.plan_lookup_key === plan.lookup_key && ["active", "trialing", "past_due"].includes(current?.status);
  const featured = plan.lookup_key === "repfleet_pro_monthly";
  return (
    <div
      className={`bg-white border rounded-sm p-5 flex flex-col ${featured ? "border-blue-500 border-2 shadow-md relative" : "border-slate-300"}`}
      data-testid={`plan-card-${plan.lookup_key}`}
    >
      {featured && (
        <div className="absolute -top-2 right-3 bg-blue-600 text-white text-[10px] font-mono uppercase font-bold px-2 py-0.5 rounded-sm">Most popular</div>
      )}
      <div className="font-display text-xl font-black tracking-tight text-slate-900">{plan.name}</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-4xl font-black tabular-nums">${plan.price}</span>
        <span className="text-sm text-slate-500 font-mono">/mo</span>
      </div>
      <div className="text-xs text-slate-600 mt-1 font-mono">
        {plan.vehicle_limit ? `Up to ${plan.vehicle_limit} vehicles` : "Unlimited vehicles"}
      </div>
      <ul className="mt-4 space-y-1.5 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-1.5 text-xs text-slate-700">
            <CheckIcon size={14} weight="bold" className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onSubscribe}
        disabled={!isAdmin || busy || isCurrent}
        className={`mt-5 rounded-sm w-full ${featured ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"} disabled:opacity-50`}
        data-testid={`plan-cta-${plan.lookup_key}`}
      >
        {isCurrent ? "Current plan" : busy ? "Redirecting…" : (current?.status && current?.status !== "none" ? "Switch to this plan" : `Start ${trialDays}-day free trial`)}
      </Button>
    </div>
  );
}
